import { useEffect, useState } from 'react';
import { getTrainingAdminCourseDetail, GetTrainingAdminCourseDetailOutputType, saveTrainingCourse, saveCourseModule, deleteCourseModule } from 'zite-endpoints-sdk';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, GripVertical, Edit, Trash2, Plus, Save, Eye, BookOpen, AlertTriangle, ChevronDown, ChevronRight, Video, Upload, Loader2, X, Link } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { uploadFile } from 'zite-file-upload-sdk';

type Module = GetTrainingAdminCourseDetailOutputType['modules'][0];
type Section = { id: string; title: string; content: string; type: string };

function SectionEditor({ sections, onChange }: { sections: Section[]; onChange: (s: Section[]) => void }) {
  const [expanded, setExpanded] = useState<string | null>(sections[0]?.id || null);

  const updateSection = (id: string, field: 'title' | 'content', value: string) => {
    onChange(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };
  const removeSection = (id: string) => onChange(sections.filter(s => s.id !== id));
  const addSection = () => {
    const newId = `s${Date.now()}`;
    onChange([...sections, { id: newId, title: '', content: '', type: 'text' }]);
    setExpanded(newId);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Lesson Sections ({sections.length})</p>
        <Button size="sm" variant="outline" onClick={addSection}><Plus className="w-3 h-3 mr-1" /> Add Section</Button>
      </div>
      {sections.map((s, i) => (
        <div key={s.id} className="border border-border rounded-lg bg-card overflow-hidden">
          <button onClick={() => setExpanded(expanded === s.id ? null : s.id)}
            className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/50 transition-colors">
            {expanded === s.id ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <span className="text-xs font-bold text-muted-foreground w-6">{i + 1}.</span>
            <span className="text-sm font-medium text-foreground flex-1 truncate">{s.title || 'Untitled Section'}</span>
            <span className="text-xs text-muted-foreground">{s.content.length} chars</span>
          </button>
          {expanded === s.id && (
            <div className="p-3 pt-0 space-y-3 border-t border-border">
              <div>
                <Label className="text-xs">Section Title</Label>
                <Input value={s.title} onChange={e => updateSection(s.id, 'title', e.target.value)} placeholder="e.g. Opening Checklist" />
              </div>
              <div>
                <Label className="text-xs">Content</Label>
                <Textarea value={s.content} onChange={e => updateSection(s.id, 'content', e.target.value)} rows={8}
                  placeholder="Write the learning content for this section…" className="font-mono text-xs" />
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant="ghost" className="text-destructive text-xs" onClick={() => removeSection(s.id)}>
                  <Trash2 className="w-3 h-3 mr-1" /> Remove Section
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
      {sections.length === 0 && (
        <button onClick={addSection} className="w-full p-4 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary/30 transition-colors">
          + Add your first lesson section
        </button>
      )}
    </div>
  );
}

export default function CourseBuilderSection({ courseId, onBack }: { courseId: string; onBack: () => void }) {
  const navigate = useNavigate();
  const [data, setData] = useState<GetTrainingAdminCourseDetailOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Draft');
  const [defaultPassMark, setDefaultPassMark] = useState(80);
  const [sequentialModules, setSequentialModules] = useState(true);
  const [reviewPeriod, setReviewPeriod] = useState('Annual');

  // Module editor
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [modLabel, setModLabel] = useState('');
  const [modLearning, setModLearning] = useState('');
  const [modMinutes, setModMinutes] = useState(0);
  const [modSafety, setModSafety] = useState('');
  const [modMistakes, setModMistakes] = useState('');
  const [modSections, setModSections] = useState<Section[]>([]);
  const [modAssessment, setModAssessment] = useState('');
  const [modVideoUrl, setModVideoUrl] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoInputMode, setVideoInputMode] = useState<'upload' | 'url'>('upload');
  const [modSaving, setModSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getTrainingAdminCourseDetail({ courseId }).then(d => {
      setData(d);
      setTitle(d.course.title);
      setCourseCode(d.course.courseCode || '');
      setDescription(d.course.description || '');
      setStatus(d.course.status || 'Draft');
      setDefaultPassMark(d.course.defaultPassMark || 80);
      setSequentialModules(d.course.sequentialModules ?? true);
      setReviewPeriod(d.course.reviewPeriod || 'Annual');
    }).catch(() => toast.error('Failed to load course')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [courseId]);

  const saveCourse = async () => {
    setSaving(true);
    try {
      await saveTrainingCourse({ id: courseId, title, courseCode: courseCode || undefined, description: description || undefined, status, defaultPassMark, sequentialModules, reviewPeriod });
      toast.success('Course saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const openEditModule = (m: Module) => {
    setEditingModule(m);
    setModLabel(m.displayLabel || '');
    setModLearning(m.learningOutcome || '');
    setModMinutes(m.estimatedMinutes || 30);
    setModSafety(m.safetyWarnings || '');
    setModMistakes(m.commonMistakes || '');
    setModAssessment(m.practicalAssessmentItems || '');
    setModVideoUrl(m.videoUrl || '');
    try { setModSections(JSON.parse(m.sectionsJson || '[]')); } catch { setModSections([]); }
    setShowModuleEditor(true);
  };

  const openAddModule = () => {
    setEditingModule(null);
    setModLabel(''); setModLearning(''); setModMinutes(30);
    setModSafety(''); setModMistakes(''); setModSections([]); setModAssessment(''); setModVideoUrl('');
    setShowModuleEditor(true);
  };

  const handleSaveModule = async () => {
    if (!modLabel.trim()) return;
    setModSaving(true);
    try {
      const nextOrder = data ? data.modules.length + 1 : 1;
      await saveCourseModule({
        id: editingModule?.id,
        courseId,
        displayLabel: modLabel,
        sortOrder: editingModule?.sortOrder ?? nextOrder,
        videoUrl: modVideoUrl || undefined,
        learningOutcome: modLearning || undefined,
        estimatedMinutes: modMinutes || undefined,
        practicalAssessmentItems: modAssessment || undefined,
        sectionsJson: JSON.stringify(modSections),
        safetyWarnings: modSafety || undefined,
        commonMistakes: modMistakes || undefined,
      });
      toast.success(editingModule ? 'Module saved' : 'Module added');
      setShowModuleEditor(false);
      load();
    } catch { toast.error('Failed to save module'); }
    setModSaving(false);
  };

  const handleDeleteModule = async (id: string) => {
    try { await deleteCourseModule({ id }); toast.success('Module deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64" /></div>;
  if (!data) return null;

  const missingCount = data.modules.reduce((c, m) => {
    let missing = 0;
    try { if (JSON.parse(m.sectionsJson || '[]').length === 0) missing++; } catch { missing++; }
    if (!m.learningOutcome) missing++;
    return c + missing;
  }, 0);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">Course Builder</p>
          <h2 className="text-lg font-bold text-foreground truncate">{title}</h2>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded ${status === 'Published' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{status}</span>
        <Button size="sm" variant="outline" onClick={() => navigate(`/train/admin/builder/${courseId}`)}>
          <BookOpen className="w-4 h-4 mr-1" /> Interactive Builder
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate(`/train/admin/builder/${courseId}`)}>
          <BookOpen className="w-4 h-4 mr-1" /> Interactive Builder
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate(`/train/admin/preview/${courseId}`)}>
          <Eye className="w-4 h-4 mr-1" /> Preview
        </Button>
        <Button size="sm" onClick={saveCourse} disabled={saving}><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
      </div>

      {/* Missing content warning */}
      {missingCount > 0 && (
        <div className="bg-muted border border-border rounded-lg p-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground">{missingCount} missing content warning{missingCount !== 1 ? 's' : ''} — click a module to edit</span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main form */}
        <div className="flex-1 space-y-4 min-w-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="text-xs text-muted-foreground">Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} /></div>
            <div><Label className="text-xs text-muted-foreground">Course Code</Label><Input value={courseCode} onChange={e => setCourseCode(e.target.value)} /></div>
          </div>
          <div><Label className="text-xs text-muted-foreground">Description</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} /></div>

          {/* Modules */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Modules ({data.modules.length})
              </h3>
              <Button size="sm" variant="outline" onClick={openAddModule}><Plus className="w-4 h-4 mr-1" /> Add Module</Button>
            </div>
            <div className="space-y-2">
              {data.modules.map((m, i) => {
                let sectionCount = 0;
                try { sectionCount = JSON.parse(m.sectionsJson || '[]').length; } catch {}
                const hasMissing = sectionCount === 0 || !m.learningOutcome;
                return (
                  <div key={m.id} className={`flex items-center gap-3 p-3 border rounded-lg bg-card group ${hasMissing ? 'border-border' : 'border-border'}`}>
                    <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{i + 1}. {m.displayLabel || 'Untitled Module'}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-0.5">
                        {m.estimatedMinutes && <span>{m.estimatedMinutes} min</span>}
                        <span>{sectionCount} section{sectionCount !== 1 ? 's' : ''}</span>
                        {m.videoUrl ? <span className="text-primary flex items-center gap-0.5"><Video className="w-3 h-3" /> Video</span> : <span className="text-muted-foreground flex items-center gap-0.5"><Video className="w-3 h-3" /> No video</span>}
                        {m.practicalAssessmentItems && <span className="text-primary">Practical</span>}
                        {hasMissing && <span className="text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Missing content</span>}
                      </div>
                    </div>
                    <button onClick={() => openEditModule(m)} className="text-muted-foreground hover:text-foreground transition-opacity p-1.5 rounded hover:bg-muted">
                      <Edit className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="text-muted-foreground hover:text-destructive transition-opacity p-1.5 rounded hover:bg-muted">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Module</AlertDialogTitle>
                          <AlertDialogDescription>This will permanently delete "{m.displayLabel}". This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteModule(m.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                );
              })}
              {data.modules.length === 0 && (
                <button onClick={openAddModule} className="w-full p-4 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-primary/30 transition-colors">
                  + Add your first module
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Settings sidebar */}
        <div className="w-full lg:w-72 shrink-0">
          <div className="border border-border rounded-lg p-4 bg-card space-y-4 sticky top-20">
            <p className="text-sm font-semibold text-foreground">Course Settings</p>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Published">Published</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Default Pass Mark (%)</Label>
              <Input type="number" value={defaultPassMark} onChange={e => setDefaultPassMark(Number(e.target.value))} min={0} max={100} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Sequential Modules</Label>
              <input type="checkbox" checked={sequentialModules} onChange={e => setSequentialModules(e.target.checked)} className="rounded border-border" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Review Period</Label>
              <Select value={reviewPeriod} onValueChange={setReviewPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Biannual">Biannual</SelectItem>
                  <SelectItem value="Annual">Annual</SelectItem>
                  <SelectItem value="On Change">On Change</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Module full-screen editor */}
      <Dialog open={showModuleEditor} onOpenChange={setShowModuleEditor}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingModule ? `Edit: ${editingModule.displayLabel}` : 'Add Module'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Display Label</Label><Input value={modLabel} onChange={e => setModLabel(e.target.value)} placeholder="e.g. Bar Setup & Mise en Place" /></div>
              <div><Label>Estimated Duration (minutes)</Label><Input type="number" value={modMinutes} onChange={e => setModMinutes(Number(e.target.value))} /></div>
            </div>
            <div><Label>Learning Outcome</Label><Textarea value={modLearning} onChange={e => setModLearning(e.target.value)} rows={2} placeholder="What will the learner achieve?" /></div>

            {/* Video */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="flex items-center gap-1.5"><Video className="w-4 h-4" /> Training Video</Label>
                <div className="flex gap-1">
                  <button onClick={() => setVideoInputMode('upload')}
                    className={`text-xs px-2 py-0.5 rounded ${videoInputMode === 'upload' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Upload className="w-3 h-3 inline mr-1" />Upload
                  </button>
                  <button onClick={() => setVideoInputMode('url')}
                    className={`text-xs px-2 py-0.5 rounded ${videoInputMode === 'url' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Link className="w-3 h-3 inline mr-1" />Paste URL
                  </button>
                </div>
              </div>

              {modVideoUrl ? (
                <div className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg">
                  <Video className="w-5 h-5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{modVideoUrl.includes('youtube') || modVideoUrl.includes('youtu.be') ? 'YouTube Video' : 'Uploaded Video'}</p>
                    <p className="text-xs text-muted-foreground truncate">{modVideoUrl}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive shrink-0" onClick={() => setModVideoUrl('')}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : videoInputMode === 'url' ? (
                <Input value={modVideoUrl} onChange={e => setModVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=... or direct MP4 URL" />
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/30 transition-colors">
                  {videoUploading ? (
                    <><Loader2 className="w-6 h-6 text-primary animate-spin" /><p className="text-sm text-muted-foreground">Uploading video…</p></>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-muted-foreground" />
                      <p className="text-sm font-medium text-foreground">Click to upload MP4, MOV or video file</p>
                      <p className="text-xs text-muted-foreground">Max 25 MB — or use "Paste URL" for YouTube links</p>
                    </>
                  )}
                  <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm" className="hidden" disabled={videoUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 25 * 1024 * 1024) {
                        toast.error('Video must be under 25 MB. Use "Paste URL" for larger files (upload to YouTube first).');
                        return;
                      }
                      setVideoUploading(true);
                      try {
                        const { fileUrl } = await uploadFile({ data: file, filename: `training-video-${Date.now()}-${file.name}` });
                        setModVideoUrl(fileUrl);
                        toast.success('Video uploaded');
                      } catch (err: any) {
                        toast.error(err?.message || 'Failed to upload video');
                      } finally {
                        setVideoUploading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
              )}
            </div>

            {/* Sections editor */}
            <SectionEditor sections={modSections} onChange={setModSections} />

            <div><Label>Safety Warnings</Label><Textarea value={modSafety} onChange={e => setModSafety(e.target.value)} rows={2} placeholder="Any safety warnings for this module…" /></div>
            <div><Label>Common Mistakes</Label><Textarea value={modMistakes} onChange={e => setModMistakes(e.target.value)} rows={2} placeholder="Common mistakes to highlight…" /></div>
            <div><Label>Practical Assessment Items (JSON)</Label><Textarea value={modAssessment} onChange={e => setModAssessment(e.target.value)} rows={3} placeholder='[{"label":"Can pour a perfect pint","evidenceRule":"Photo"}]' /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleEditor(false)}>Cancel</Button>
            <Button disabled={!modLabel.trim() || modSaving} onClick={handleSaveModule}>
              {modSaving ? 'Saving…' : editingModule ? 'Save Module' : 'Add Module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
